/**
 * EntityStep Component
 * Step to create the first Swiss entity during setup
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CheckCircle,
  AlertCircle,
  Loader2,
  Search,
  SkipForward,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ZefixSearch } from '@/components/entities/ZefixSearch';
import { useTenant } from '@/contexts/TenantContext';
import { toast } from '@/components/ui/Toast';
import {
  validateSwissUid,
  type UidValidationResult,
} from '@/utils/validation/swiss-uid';
import type { SetupEntityData } from '../types';

// Swiss cantons
const CANTONS = [
  'AG', 'AI', 'AR', 'BE', 'BL', 'BS', 'FR', 'GE', 'GL', 'GR',
  'JU', 'LU', 'NE', 'NW', 'OW', 'SG', 'SH', 'SO', 'SZ', 'TG',
  'TI', 'UR', 'VD', 'VS', 'ZG', 'ZH'
] as const;

// Legal forms
const LEGAL_FORMS = [
  { value: 'Aktiengesellschaft', label: 'Aktiengesellschaft (AG)' },
  { value: 'GmbH', label: 'GmbH' },
  { value: 'Einzelunternehmen', label: 'Einzelunternehmen' },
  { value: 'Genossenschaft', label: 'Genossenschaft' },
  { value: 'Stiftung', label: 'Stiftung' },
  { value: 'Verein', label: 'Verein' },
  { value: 'Other', label: 'Other' },
] as const;

// Custom Zod refinement for Swiss UID validation
const swissUidSchema = z.string().superRefine((val, ctx) => {
  const result = validateSwissUid(val);
  if (!result.valid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: result.error || 'Invalid Swiss UID',
    });
  }
});

// Form validation schema
const entitySchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  uid: swissUidSchema,
  legal_form: z.enum(['Aktiengesellschaft', 'GmbH', 'Einzelunternehmen', 'Genossenschaft', 'Stiftung', 'Verein', 'Other']),
  registration_canton: z.enum(CANTONS),
  registration_date: z.string().min(1, 'Registration date is required'),
  status: z.enum(['active', 'inactive', 'archived', 'dissolved']).default('active'),
  purpose: z.string().optional(),
  capital: z.string().optional(),
});

type EntityFormData = z.infer<typeof entitySchema>;

interface EntityStepProps {
  data: SetupEntityData | null;
  onChange: (data: SetupEntityData | null) => void;
  onContinue: () => void;
  onBack: () => void;
  onSkip: () => void;
}

export const EntityStep: React.FC<EntityStepProps> = ({
  data,
  onChange,
  onContinue,
  onBack,
  onSkip,
}) => {
  const { tenant } = useTenant();
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [importedFromZefix, setImportedFromZefix] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [entityCreated, setEntityCreated] = useState(false);
  const [uidValidation, setUidValidation] = useState<UidValidationResult | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<EntityFormData>({
    resolver: zodResolver(entitySchema) as never,
    defaultValues: (data as EntityFormData) || {
      status: 'active',
      registration_date: new Date().toISOString().split('T')[0],
    },
  });

  const watchUid = watch('uid');

  // Real-time UID validation
  useEffect(() => {
    if (!watchUid || watchUid.length === 0) {
      setUidValidation(null);
      return;
    }
    if (watchUid.length >= 9) {
      const result = validateSwissUid(watchUid);
      setUidValidation(result);
    } else {
      setUidValidation(null);
    }
  }, [watchUid]);

  // Format UID as user types
  const handleUidChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    if (cleaned.startsWith('CHE')) {
      const digits = cleaned.slice(3).replace(/\D/g, '');
      let formatted = 'CHE-';
      for (let i = 0; i < digits.length && i < 9; i++) {
        if (i === 3 || i === 6) formatted += '.';
        formatted += digits[i];
      }
      setValue('uid', formatted, { shouldValidate: digits.length >= 9 });
    } else if (/^\d/.test(cleaned)) {
      const digits = cleaned.replace(/\D/g, '');
      let formatted = 'CHE-';
      for (let i = 0; i < digits.length && i < 9; i++) {
        if (i === 3 || i === 6) formatted += '.';
        formatted += digits[i];
      }
      setValue('uid', formatted, { shouldValidate: digits.length >= 9 });
    } else {
      setValue('uid', value.toUpperCase());
    }
  }, [setValue]);

  // Handle Zefix company selection
  const handleZefixSelect = (company: {
    uid: string;
    name: string;
    legalForm: string;
    canton: string;
    registrationDate: string;
    purpose?: string;
    capital?: string;
    status: 'active' | 'inactive' | 'dissolved';
  }) => {
    const legalFormMap: Record<string, EntityFormData['legal_form']> = {
      'Aktiengesellschaft': 'Aktiengesellschaft',
      'GmbH': 'GmbH',
      'Einzelunternehmen': 'Einzelunternehmen',
      'Genossenschaft': 'Genossenschaft',
      'Stiftung': 'Stiftung',
      'Verein': 'Verein',
    };

    const mappedLegalForm = legalFormMap[company.legalForm] || 'Other';

    setValue('name', company.name);
    setValue('uid', company.uid);
    setValue('legal_form', mappedLegalForm);
    setValue('registration_canton', company.canton as typeof CANTONS[number]);
    if (company.registrationDate) {
      setValue('registration_date', company.registrationDate);
    }
    if (company.purpose) {
      setValue('purpose', company.purpose);
    }
    if (company.capital) {
      setValue('capital', company.capital);
    }
    setValue('status', company.status || 'active');

    setImportedFromZefix(true);
    setShowManualEntry(true);
    toast.success('Company data imported from Zefix');
  };

  const onSubmit = async (formData: EntityFormData) => {
    setIsSubmitting(true);

    try {
      const response = await fetch('/api/entities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenant?.id || '',
        },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          tenant_id: tenant?.id,
        }),
      });

      if (!response.ok) {
        const error = (await response.json()) as { message?: string };
        throw new Error(error.message || 'Failed to create entity');
      }

      const result = await response.json();

      // Update parent with created entity
      onChange(formData as SetupEntityData);
      setEntityCreated(true);
      toast.success('Entity created!', {
        description: `${formData.name} has been registered.`,
      });

      // Wait a moment then continue
      setTimeout(() => {
        onContinue();
      }, 1500);
    } catch (error) {
      toast.error('Failed to create entity', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show success state if entity was created
  if (entityCreated) {
    return (
      <div className="flex flex-col items-center text-center py-8">
        <div className="w-16 h-16 rounded-full bg-success-100 dark:bg-success-900/30 flex items-center justify-center mb-4">
          <CheckCircle className="h-8 w-8 text-success-600 dark:text-success-400" />
        </div>
        <h3 className="text-lg font-semibold text-espresso-900 dark:text-cream-100 mb-2">
          Entity Created!
        </h3>
        <p className="text-espresso-500 dark:text-espresso-300 mb-4">
          Your first Swiss entity has been registered.
        </p>
        <Loader2 className="h-5 w-5 animate-spin text-terracotta-600" />
      </div>
    );
  }

  return (
    <div>
      {/* Header icon */}
      <div className="flex justify-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-terracotta-100 dark:bg-terracotta-900/30 flex items-center justify-center">
          <Building2 className="h-8 w-8 text-terracotta-600 dark:text-terracotta-400" />
        </div>
      </div>

      {/* Zefix Search (initial view) */}
      {!showManualEntry && (
        <div className="space-y-4">
          <ZefixSearch onSelect={handleZefixSelect} />

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={() => setShowManualEntry(true)}
              className="text-sm text-espresso-500 hover:text-terracotta-600 transition-colors"
            >
              or enter details manually
            </button>
          </div>

          {/* Skip option */}
          <div className="pt-6 border-t border-cream-200 dark:border-espresso-600">
            <div className="flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={onBack}
                className="text-espresso-600 dark:text-espresso-300"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={onSkip}
                className="text-espresso-500"
              >
                <SkipForward className="h-4 w-4 mr-2" />
                Skip for now
              </Button>
            </div>
            <p className="text-xs text-espresso-400 text-center mt-3">
              You can always add entities later from the Entities page.
            </p>
          </div>
        </div>
      )}

      {/* Manual Entry Form */}
      {showManualEntry && (
        <form onSubmit={handleSubmit(onSubmit as never)} className="space-y-4">
          {/* Back to search */}
          <div className="flex items-center justify-between mb-4">
            {importedFromZefix ? (
              <div className="inline-flex items-center gap-2 text-sm text-success-600 bg-success-50 dark:bg-success-900/30 px-3 py-1.5 rounded-lg">
                <CheckCircle className="h-4 w-4" />
                <span>Imported from Zefix</span>
              </div>
            ) : (
              <div className="text-sm text-espresso-400">Manual entry</div>
            )}
            <button
              type="button"
              onClick={() => {
                setShowManualEntry(false);
                setImportedFromZefix(false);
                reset({
                  status: 'active',
                  registration_date: new Date().toISOString().split('T')[0],
                });
              }}
              className="inline-flex items-center gap-2 text-sm text-espresso-500 hover:text-terracotta-600 transition-colors"
            >
              <Search className="h-4 w-4" />
              Search again
            </button>
          </div>

          {/* Entity Name */}
          <div>
            <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
              Entity Name *
            </label>
            <input
              {...register('name')}
              className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 dark:focus:ring-terracotta-800 transition-colors"
              placeholder="Example AG"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-error-600">{errors.name.message}</p>
            )}
          </div>

          {/* UID and Legal Form */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
                Swiss UID *
              </label>
              <div className="relative">
                <input
                  {...register('uid')}
                  onChange={handleUidChange}
                  className={`w-full px-4 py-2.5 pr-10 rounded-lg border font-mono transition-colors ${
                    uidValidation?.valid
                      ? 'border-success-500 focus:border-success-500 focus:ring-2 focus:ring-success-200'
                      : errors.uid
                      ? 'border-error-500 focus:border-error-500 focus:ring-2 focus:ring-error-200'
                      : 'border-cream-300 dark:border-espresso-600 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200'
                  } bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100`}
                  placeholder="CHE-123.456.789"
                  maxLength={16}
                />
                {uidValidation && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {uidValidation.valid ? (
                      <CheckCircle className="h-5 w-5 text-success-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-error-500" />
                    )}
                  </div>
                )}
              </div>
              {errors.uid && (
                <p className="mt-1 text-xs text-error-600">{errors.uid.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
                Legal Form *
              </label>
              <select
                {...register('legal_form')}
                className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 transition-colors"
              >
                <option value="">Select...</option>
                {LEGAL_FORMS.map((form) => (
                  <option key={form.value} value={form.value}>
                    {form.label}
                  </option>
                ))}
              </select>
              {errors.legal_form && (
                <p className="mt-1 text-xs text-error-600">{errors.legal_form.message}</p>
              )}
            </div>
          </div>

          {/* Canton and Registration Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
                Canton *
              </label>
              <select
                {...register('registration_canton')}
                className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 transition-colors"
              >
                <option value="">Select...</option>
                {CANTONS.map((canton) => (
                  <option key={canton} value={canton}>
                    {canton}
                  </option>
                ))}
              </select>
              {errors.registration_canton && (
                <p className="mt-1 text-xs text-error-600">{errors.registration_canton.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-espresso-700 dark:text-espresso-200 mb-1.5">
                Registration Date *
              </label>
              <input
                {...register('registration_date')}
                type="date"
                className="w-full px-4 py-2.5 rounded-lg border border-cream-300 dark:border-espresso-600 bg-white dark:bg-espresso-800 text-espresso-900 dark:text-cream-100 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 transition-colors"
              />
              {errors.registration_date && (
                <p className="mt-1 text-xs text-error-600">{errors.registration_date.message}</p>
              )}
            </div>
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center justify-between pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onBack}
              className="text-espresso-600 dark:text-espresso-300"
              disabled={isSubmitting}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>

            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onSkip}
                className="text-espresso-500"
                disabled={isSubmitting}
              >
                Skip
              </Button>

              <Button
                type="submit"
                className="bg-terracotta-600 hover:bg-terracotta-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Entity
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default EntityStep;
